import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import * as L from 'leaflet';
import { latLng, tileLayer } from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.gridlayer.googlemutant';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { combineLatest, concat, from, Observable, of, Subject, Subscription } from 'rxjs';
import { DataService } from '../../services/data.service';
import { UserService } from '../../services/user.service';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { RespondTicketComponent } from '../modals/respond-ticket/respond-ticket.component';
import { AppConfig } from "../../app.config";
import { TicketImagesModalComponent } from '../modals/ticket-images/ticket-images-modal.component';
import { StreetViewService } from '../../services/street-view.service';
import { InfoModalComponent } from '../modals/info-modal/info-modal.component';
import _ from "lodash";
import { GeocodingService } from 'src/app/services/geocoding.service';
import { ErrorModalComponent } from '../modals/error-modal/error-modal.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, AfterViewInit {

  optShown: boolean = false;
  popupShown: boolean = false;

  leafletOptions: any;
  layers: any[];

  projectId: number = 72;
  isChildProject: boolean = false;

  private map: L.DrawMap;

  markerCluster;
  schoolMarkerCluster;

  private oldActiveMarker;
  private oldImgMarker;

  legend: any[] = [];
  flattenLegend: any[] = [];
  markerPopup: any;

  dict: any;
  mapLoading: Subscription;

  tiles: any[] = [];
  currentTileIdx: number = 1;

  address$: Observable<any[]>;
  addressLoading: boolean = false;
  addressInput$ = new Subject<string>();

  projectName = environment.projectName

  constructor(
    private cd: ChangeDetectorRef,
    private dataService: DataService,
    private userService: UserService,
    private modalService: NgbModal,
    private config: AppConfig,
    private gsv: StreetViewService, // DO NOT REMOVE!
    private geocodingService: GeocodingService,
  ) {
  }

  ngOnInit() {

    this.projectId = this.userService.project.projectID.id;
    this.isChildProject = 'parentID' in this.userService.project.project.projectData;

    this.userService.getUserInfo().subscribe(user => this.addHomeMarker());

    let tileLayerOptions = {
      // attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
      maxZoom: 18,
      id: 'mapbox.streets',
      accessToken: 'pk.eyJ1IjoiYW50b242Mzc4IiwiYSI6IlBtMG5pMjgifQ.bGiCZKEStTnO5YjSzmkJZg'
    };

    this.tiles = [
      // tileLayer('https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}', tileLayerOptions),
      // @ts-ignore
      L.gridLayer.googleMutant({ type: 'satellite' }),
      this.config.getTileLayer()
    ];
    let pos;
    try {
      const center = _.cloneDeep(this.userService.project.project.projectData.center);
      pos = center ? latLng(center.coordinates.reverse()) : latLng(50.848656786354994, 4.349341392517091);
    } catch {
      pos = latLng(50.848656786354994, 4.349341392517091);
    }

    this.leafletOptions = {
      layers: [
        this.tiles[1]
      ],
      zoom: 14,
      center: pos
    };

    this.map = L.map('map', this.leafletOptions);

    this.map.on('click', (e) => {
      this.optShown = false;
    });

    // @ts-ignore
    this.markerCluster = L.markerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: this.clusterIcon,
      // disableClusteringAtZoom: 18
    });

    // @ts-ignore
    this.schoolMarkerCluster = L.markerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: this.schoolClusterIcon,
    });

    this.map.addLayer(this.markerCluster);
    this.map.addLayer(this.schoolMarkerCluster);

    this.getLegend();
    this.initTypeahead();
  }

  initTypeahead() {
    const project_location = localStorage.getItem('project_location');
    this.address$ = concat(
      of([]),
      this.addressInput$.pipe(
        distinctUntilChanged(),
        tap(() => this.addressLoading = true),
        switchMap(term => {
          if (term) {
            console.log(this.map.getCenter());
            let latlng;
            if (!project_location) { latlng = this.map.getCenter(); }
            else {
              const locations = project_location.split(',');
              var latitude = parseFloat(locations[1]),
                longitude = parseFloat(locations[0]);
              latlng = L.latLng(latitude, longitude);
            }
            return this.geocodingService.addressAutocomplete(term, latlng.lat, latlng.lng).pipe(
              catchError(() => of([])),
              map(result => {
                if (result.features) return result.features.map(r => Object.assign(r.properties, r.geometry));
                else return [];
              }),
              tap(() => this.addressLoading = false)
            );
          }
          this.addressLoading = false;
          // return of([]).pipe(
          //   map(() => this.addressLoading = false)
          // );
        })
      )
    )
  }

  findPos(latlng) {
    this.map.panTo({ lng: latlng.lon ? latlng.lon : latlng.lng, lat: latlng.lat });
    this.map.setZoom(18);
  }

  onInputAddress(event) {
    console.log('input', event);
    if (event.coordinates) {
      const latlng = L.latLng(event.coordinates[1], event.coordinates[0]);
      this.findPos(latlng);
    }
    else if (event.name) {
      this.geocodingService.findPosition(event.name).subscribe(response => {
        if (response && response.lat) this.findPos(response);
      }, err => {
        const errDlg = this.modalService.open(ErrorModalComponent);
        errDlg.componentInstance.header = 'Unknown address';
        errDlg.componentInstance.message = 'The location of this address could not be determined. Please enter a new address.';
      });
    }
  }

  getImgAbsoluteUrl(imgId) {
    if (imgId.indexOf('assets') != -1) return imgId;
    else return `${this.config.getHost()}/api2/public/image?image_id=${imgId}`;
  }

  ngAfterViewInit(): void {

  }

  openAttachModal(id) {
    const options: NgbModalOptions = {
      backdrop: true,
      keyboard: false,
      centered: true,
      windowClass: 'attachImageModal'
    };

    const dlg = this.modalService.open(TicketImagesModalComponent, options);
    dlg.componentInstance.images = this.markerPopup.image;
    dlg.componentInstance.selectedID = id;
  }

  toggleOptions() {
    this.optShown = !this.optShown;
  }

  toggleMapType() {
    try {
      this.tiles[this.currentTileIdx].removeFrom(this.map);
    } catch { }
    // this.currentTileIdx = this.currentTileIdx == 2 ? 0 : this.currentTileIdx + 1;
    this.currentTileIdx = this.currentTileIdx == 1 ? 0 : this.currentTileIdx + 1;
    this.tiles[this.currentTileIdx].addTo(this.map);
  }

  closePopup() {
    this.popupShown = false;

    if (this.oldActiveMarker && this.oldImgMarker) {
      this.oldActiveMarker.setIcon(L.icon({
        iconUrl: `assets/img/${this.oldImgMarker}`,
        iconSize: [47, 57],
        iconAnchor: L.point(23, 57)
      }));
    }
  }

  respondTicket(ticket) {
    this.dataService
      .isLikedTicket(ticket.projectID.id, ticket.entityID.id2)
      .pipe(
        switchMap(isLike => {
          const dlg = this.modalService.open(RespondTicketComponent);
          dlg.componentInstance.isLiked = isLike;
          dlg.componentInstance.isLikedAlready = isLike;
          return from(dlg.result);
        }),
        switchMap(dlgAnswer => {
          if (!dlgAnswer.isLikedAlready && dlgAnswer.like) {
            this.dataService.likeTicket(ticket.projectID.id, ticket.entityID.id2).subscribe(() => of(dlgAnswer));
          } else {
            return of(dlgAnswer);
          }
        }),
        switchMap(dlgAnswer => {
          if (dlgAnswer.canceled) {
            return of('canceled');
          }
          return this.dataService.addMessageToTicket(ticket.projectID.id, ticket.entityID.id2, dlgAnswer.msg);
        })
      )
      .subscribe(response => {
        if (response !== 'canceled') {
          const infoDlg = this.modalService.open(InfoModalComponent);
          infoDlg.componentInstance.data = {
            text: 'Your reaction has been sent',
            header: 'Message'
          };
        }
      });
  }

  getLegend() {

    this.mapLoading = combineLatest([
      this.dataService.getDictionary('ENG'),
      this.dataService.getLayerSets(this.projectId),
      this.dataService.getLayers(this.projectId),
      this.dataService.genRouteInProject(this.projectId)
    ]).pipe(
      switchMap(([dict, layerSets, layers, genRoutes]) => {
        console.log('genroutes', genRoutes.filter(r => r.segments.length > 1));

        this.legend = [];

        this.dict = dict;

        let entities$ = [];
        layerSets.forEach(s => {
          this.legend.push({
            value: true,
            name: `set_${s.layerSetID.id2}`,
            setId: s.layerSetID.id2,
            key: s.name.toLowerCase(),
            label: dict[s.name.toLowerCase()],
            icon: s.icon.id,
            children: [],
            no_icon: ['LS_TICKETS', 'LS_ROUTES'].includes(s.name)
          });
        });

        layers.forEach(l => {
          if (l.default_visible) {
            if (this.isChildProject && !l.visible_in_sub_projects) return;
            let setId = null;
            let legend = null;

            if (l.layer_set_id) {
              setId = l.layer_set_id.id2;
              legend = this.legend.find(s => s.setId == l.layer_set_id.id2).children;
            } else {
              legend = this.legend;
            }

            let colorMap = null;
            let iconMap = null;
            let tagMap = {};

            if (l.style_descriptor.dynamic_icon && setId) {
              iconMap = l.style_descriptor.dynamic_icon.map;
              Object.keys(iconMap).forEach(k => {

                let item = {
                  value: true,
                  name: `layer_${l.default_entity_type}_${l.layerID.id2}_${k}`,
                  setId: setId,
                  key: k.toLowerCase(),
                  label: dict[k.toLowerCase()],
                  icon: `assets/img/${iconMap[k]}`,
                  target: this.markerCluster,
                  entities: []
                };
                tagMap[k] = item.entities;
                legend.push(item);
                this.flattenLegend.push(item);
              });

              entities$.push(this.dataService.getEntities(this.projectId, l.layerID.id2, l.layerID.id1.id, false).pipe(
                catchError(() => of('err')),
                tap((result: any[] | string) => {
                  if (!Array.isArray(result)) {
                    if (result === 'err') {
                      console.log(result)
                    }
                  } else {
                    result.forEach(e => {
                      if ((e.projectID && e.projectID.id === this.projectId)
                        || (e.genericAttributes.attributes &&
                          e.genericAttributes.attributes.projectID &&
                          e.genericAttributes.attributes.projectID.length &&
                          e.genericAttributes.attributes.projectID[0].id === this.projectId)) {
                        let attrs = Object.assign({ entityID: e.entityID }, { projectID: e.projectID }, e.genericAttributes.attributes);
                        let key = attrs.status[0];

                        let hidden = attrs.invisible_in_project
                          && attrs.invisible_in_project.length > 0
                          && attrs.invisible_in_project.indexOf(this.projectId) >= 0;
                        if (!hidden) {
                          let marker = this.marker(attrs, iconMap[key], e.timeStamp);
                          this.markerCluster.addLayer(marker);
                          tagMap[key].push(marker);
                        }
                      }
                    });
                  }
                })
              ));


            } else if (l.style_descriptor.dynamic_color && setId) {
              colorMap = l.style_descriptor.dynamic_color.map;

              Object.keys(colorMap).forEach(k => {
                let item = {
                  value: true,
                  name: `layer_${l.default_entity_type}_${l.layerID.id2}_${k}`,
                  setId: setId,
                  key: k.toLowerCase(),
                  label: dict[k.toLowerCase()],
                  line: colorMap[k],
                  target: this.map,
                  entities: []
                };
                tagMap[k] = item.entities;
                this.flattenLegend.push(item);
                legend.push(item)
              });

              let routes$ = genRoutes && genRoutes.length ? of(genRoutes) : this.dataService.getEntities(this.projectId, l.layerID.id2, l.layerID.id1.id, false);

              console.log(routes$);

              entities$.push(routes$.pipe(
                catchError(() => of('err')),
                tap((result: any[] | string) => {
                  if (!Array.isArray(result)) {
                    if (result === 'err') {
                      console.log(result)
                    }
                  } else {
                    result.forEach(e => {
                      if ((e.projectID && e.projectID.id === this.projectId)
                        || (e.genericAttributes.attributes &&
                          e.genericAttributes.attributes.projectID &&
                          e.genericAttributes.attributes.projectID.length &&
                          e.genericAttributes.attributes.projectID[0].id === this.projectId)) {
                        e.segments.forEach(s => {
                          let attrs = s.genericAttributes.attributes;
                          let key = attrs.accessibility[0];
                          let hidden = attrs.invisible_in_project
                            && attrs.invisible_in_project.length > 0
                            && attrs.invisible_in_project.indexOf(this.projectId) >= 0;
                          if (!hidden) {
                            let polyline = this.poly(attrs, colorMap[key]);
                            tagMap[key].push(polyline.addTo(this.map));
                          }

                        });
                      }
                    });
                  }
                })
              ));
            } else if (l.name !== 'GenRoutes') {
              let key = l.name.toLowerCase();
              iconMap = l.style_descriptor;

              let item = {
                value: true,
                name: `layer_${l.default_entity_type}_${l.layerID.id2}_${key}`,
                setId: setId,
                key: key,
                label: l.name,
                target: null,
                entities: []
              };
              if (l.default_geom_type === 'PUNTAL') {
                item['icon'] = iconMap.style_point ? `assets/img/${iconMap.style_point.icon}` : '';
              } else if (l.default_geom_type === 'LINEAL') {
                item['line'] = iconMap.style_line ? iconMap.style_line.color : '';
              } else if (l.default_geom_type === 'POLYGONAL') {
                item['line'] = iconMap.style_polygon ? iconMap.style_polygon.color : '';
              }

              let iconUrl = iconMap.style_point ? `assets/marker/${iconMap.style_point.icon}` : '';
              let commonCluster = null;
              if (l.name === 'Scholen') {
                iconUrl = `assets/marker/office.png`;
                item['icon'] = iconUrl;
                item['target'] = this.schoolMarkerCluster;
              } else {
                // Common Cluster
                // @ts-ignore
                commonCluster = L.markerClusterGroup({
                  spiderfyOnMaxZoom: true,
                  showCoverageOnHover: false,
                  iconCreateFunction: this.commonClusterIcon,
                });
                item['target'] = commonCluster;
                this.map.addLayer(commonCluster);
              }

              legend.push(item);
              tagMap[key] = item.entities;
              this.flattenLegend.push(item);

              if (l.default_geom_type === 'PUNTAL') {
                entities$.push(this.dataService.getEntities(this.projectId, l.layerID.id2, l.layerID.id1.id, false).pipe(
                  catchError(() => of('err')),
                  tap((result: any[] | string) => {
                    if (!Array.isArray(result)) {
                      if (result === 'err') {
                        console.log(result)
                      }
                    } else {
                      result.forEach(e => {
                        if ((e.projectID && e.projectID.id === this.projectId)
                          || (e.genericAttributes.attributes &&
                            e.genericAttributes.attributes.projectID &&
                            e.genericAttributes.attributes.projectID.length &&
                            e.genericAttributes.attributes.projectID[0].id === this.projectId)) {
                          let attrs = Object.assign({ entityID: e.entityID }, { projectID: e.projectID }, e.genericAttributes.attributes);
                          let marker = L.marker(this.latLng(attrs.geometry[0].coordinates), {
                            icon: L.icon({
                              iconUrl: iconUrl,
                              iconAnchor: L.point(29, 67)
                            })
                          });
                          if (key === "scholen" && attrs.name) marker.bindTooltip(attrs.name[0]);
                          let hidden = attrs.invisible_in_project
                            && attrs.invisible_in_project.length > 0
                            && attrs.invisible_in_project.indexOf(this.projectId) >= 0;
                          if (!hidden) {
                            if (key === "scholen") {
                              this.schoolMarkerCluster.addLayer(marker)
                            } else {
                              commonCluster.addLayer(marker)
                            }
                            tagMap[key].push(marker);
                          }
                        }
                      });
                    }
                  })
                ));
              }
              else if (l.default_geom_type === 'LINEAL') {
                let routes$ = this.dataService.getEntities(this.projectId, l.layerID.id2, l.layerID.id1.id, false);

                entities$.push(routes$.pipe(
                  catchError(() => of('err')),
                  tap((result: any[] | string) => {
                    if (!Array.isArray(result)) {
                      if (result === 'err') {
                        console.log(result)
                      }
                    } else {
                      result.forEach(e => {

                        if ((e.projectID && e.projectID.id === this.projectId)
                          || (e.genericAttributes.attributes &&
                            e.genericAttributes.attributes.projectID &&
                            e.genericAttributes.attributes.projectID.length &&
                            e.genericAttributes.attributes.projectID[0].id === this.projectId)) {
                          let attrs = e.genericAttributes.attributes;

                          let hidden = attrs.invisible_in_project
                            && attrs.invisible_in_project.length > 0
                            && attrs.invisible_in_project.indexOf(this.projectId) >= 0;
                          if (!hidden) {
                            let polyline = this.poly(attrs, iconMap.style_line.color);
                            tagMap[key].push(polyline.addTo(this.map));
                          }
                        }
                      });
                    }
                  })
                ));
              }
            }
          }
        });

        return combineLatest(entities$);

      })
    ).subscribe();
  }

  updateByLegend() {
    this.flattenLegend.forEach(l => {
      l.entities.forEach(e => {
        if (l.target) l.target.removeLayer(e);
        else this.map.removeLayer(e);
        if (l.value) {
          if (l.target) l.target.addLayer(e);
          else this.map.addLayer(e);
        }
      })
    });
  }

  legendChanged(legend) {
    if ('children' in legend && legend.children.length) {
      legend.children.forEach(sl => sl.value = legend.value);
    }
    this.updateByLegend();
  }

  subLegendChanged(parent) {
    parent.value = parent.children.reduce((flag, sl) => flag && sl.value, true);
    this.updateByLegend();
  }

  private marker(attrs: any, icon: string, timestamp) {
    let marker = L.marker(this.latLng(attrs.geometry[0].coordinates), {
      status: attrs.status[0],
      icon: L.icon({
        iconUrl: `assets/img/${icon}`,
        iconAnchor: L.point(23, 57)
      })
    } as L.MarkerOptions);

    marker.on('click', e => {
      if (this.oldActiveMarker && this.oldImgMarker) {
        this.oldActiveMarker.setIcon(L.icon({
          iconUrl: `assets/img/${this.oldImgMarker}`,
          iconSize: [47, 57],
          iconAnchor: L.point(23, 57)
        }));
      }
      this.markerPopup = Object.assign({}, attrs, { timestamp: timestamp });
      this.popupShown = true;
      const myIcon = L.icon({
        iconUrl: `assets/img/${icon}`,
        iconSize: [77, 92],
        iconAnchor: L.point(34, 67)
      });
      e.target.setIcon(myIcon);
      this.oldActiveMarker = e.target;
      this.oldImgMarker = icon;
    });

    return marker;
  }

  private poly(attr: any, color: string) {
    let coords = attr.geometry[0].coordinates.map(coords => [coords[1], coords[0]]);
    return L.polyline(coords, {
      color: color
    })
  }

  private latLng(ll) {
    return L.latLng(ll[1], ll[0]);
  }

  clusterIcon(cluster) {
    const markers = cluster.getAllChildMarkers();

    let types = [];

    markers.forEach(m => {
      let attr = m.options['status'];
      switch (attr) {
        case 'REPORTED':
          types[0] = 'r';
          break;
        case 'COMPLETED':
          types[1] = 'g';
          break;
        case 'PROCESSING':
          types[2] = 'b';
      }
    });

    let colorClass = types.join('');
    return L.divIcon({ html: markers.length, className: `cluster cluster-${colorClass}`, iconSize: L.point(47, 47) });
  }

  schoolClusterIcon(cluster) {
    const markers: Array<L.Marker> = cluster.getAllChildMarkers();
    return L.divIcon({ html: `<span class='badge badge-office'>${markers.length}</span>`, className: `cluster cluster-office`, iconSize: L.point(57, 67) });
  }

  commonClusterIcon(cluster) {
    const markers: Array<L.Marker> = cluster.getAllChildMarkers();
    let icon: L.Icon<L.IconOptions> | L.DivIcon = null;
    if (markers.length > 0) {
      const marker = markers[0];
      icon = marker.getIcon();
    }
    return L.divIcon({ html: `<img src="${icon && icon.options.iconUrl}"><span class='badge badge-common'>${markers.length}</span>`, className: `cluster cluster-common`, iconSize: L.point(32, 32) });
  }

  addHomeMarker() {
    let userLocation = this.userService.user.attributes.location[0].coordinates;
    var homeMarker = L.marker(new L.LatLng(userLocation[1], userLocation[0]), {
      icon: L.icon({ iconUrl: 'assets/marker/home.png', iconSize: [57, 67], iconAnchor: [27, 66] }),
      draggable: true
    });

    homeMarker.bindTooltip(`${this.userService.user.attributes.address[0].street} ${this.userService.user.attributes.address[0].houseNumber}`);

    this.map.addLayer(homeMarker);
  }

}
